"use client";

import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Pagination } from "@/src/components/common/Pagination";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import RankedArticleList, {
  rankingCaption,
} from "@/src/components/journal-site/ranked-articles/ranked-article-list";
import RankedArticle from "@/src/_types/Article/ranked-article";
import VISUALLY_HIDDEN from "@/src/utils/visually-hidden";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  HEADING_SIZE,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";

const PER_PAGE = 10;

export default function MostCitedArticles({
  articles,
  title,
  description,
  showJournal,
}: {
  articles: RankedArticle[];
  title: string;
  description: string;
  showJournal: boolean;
}) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(articles.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const visible = useMemo(
    () => articles.slice((current - 1) * PER_PAGE, current * PER_PAGE),
    [articles, current],
  );

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{
          pt: { xs: 1, md: 1.5 },
          pb: { xs: 0.75, md: 1 },
          ...doubleRule,
        }}
      >
        <Kicker sx={{ mb: 0.25 }}>Citation data</Kicker>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { lg: "flex-end" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              {title}
            </Typography>
            <Typography
              component="p"
              sx={{
                m: 0,
                mt: 0.25,
                fontFamily: FONT_TEXT,
                fontSize: { xs: "0.9375rem", md: "1rem" },
                lineHeight: 1.6,
                color: "custom.inkMuted",
                maxWidth: "56ch",
              }}
            >
              {description}
            </Typography>
          </Box>
          <MetaLine
            sx={{ flex: "none" }}
            items={[
              `${articles.length} article${articles.length === 1 ? "" : "s"}`,
              rankingCaption("citations"),
              totalPages > 1 ? `Page ${current} of ${totalPages}` : null,
            ]}
          />
        </Box>
      </Box>

      <Box
        component="section"
        aria-labelledby="most-cited-list-heading"
        sx={{ pt: { xs: 0.5, md: 0.75 } }}
      >
        <Typography
          component="h2"
          id="most-cited-list-heading"
          sx={VISUALLY_HIDDEN}
        >
          Ranked articles
        </Typography>
        <RankedArticleList
          metric="citations"
          articles={visible}
          showJournal={showJournal}
        />
      </Box>

      <Pagination
        currentPage={current}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </Box>
  );
}
